import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { SectionCard } from '../common/ui';
import ConfirmDialog from '../common/ConfirmDialog';
import PaymentDialog from './PaymentDialog';
import JobService from '../../services/JobService';
import { errorMessage } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { fmtDate, fmtMoney } from '../../utils/format';

function Figure({ label, value, color = '#0F172A', sub }) {
    return (
        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</Typography>
            <Typography noWrap sx={{ fontSize: 17, fontWeight: 800, color, mt: 0.25 }}>{value}</Typography>
            {sub && <Typography sx={{ fontSize: 11.5, color: '#94A3B8' }}>{sub}</Typography>}
        </Box>
    );
}

/**
 * Price, payments and what is still owed. Hidden behind the password (`money` from useMoneyLock) so a laptop
 * left signed in does not show the amounts; "Hide" or leaving the job hides them again.
 */
export default function PaymentsCard({ job, money, onJobChange }) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const { visible, show, hide } = money;
    const [payments, setPayments] = useState([]);
    const [addOpen, setAddOpen] = useState(false);
    const [toDelete, setToDelete] = useState(null);

    const load = useCallback(() => {
        if (!visible) { setPayments([]); return; }
        JobService.payments(job.id).then(setPayments).catch(() => {});
    }, [job.id, visible]);
    useEffect(() => { load(); }, [load, job.updatedAt]);

    const add = async (payload) => {
        try {
            onJobChange(await JobService.addPayment(job.id, payload));
            showToast(t('payment.added'), 'success');
            setAddOpen(false);
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
        }
    };

    const remove = async () => {
        try {
            onJobChange(await JobService.deletePayment(job.id, toDelete.id));
            showToast(t('payment.deleted'), 'success');
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
        } finally {
            setToDelete(null);
        }
    };

    const action = visible && (
        <Button size="small" startIcon={<VisibilityOffOutlinedIcon />} onClick={hide} sx={{ color: '#64748B' }}>
            {t('money.hide')}
        </Button>
    );

    if (!visible) {
        return (
            <SectionCard title={t('payment.title')} sx={{ mb: 2.5 }}>
                <Box sx={{ px: 2.5, py: 3, textAlign: 'center' }}>
                    <LockOutlinedIcon sx={{ color: '#CBD5E1', fontSize: 34 }} />
                    <Typography sx={{ fontSize: 13.5, color: '#64748B', mt: 0.5, mb: 1.5 }}>{t('payment.hiddenText')}</Typography>
                    <Button variant="outlined" startIcon={<VisibilityOutlinedIcon />} onClick={show}>{t('money.show')}</Button>
                </Box>
            </SectionCard>
        );
    }

    const out = job.outstanding != null ? Number(job.outstanding) : null;
    const outColor = out == null ? '#94A3B8' : out === 0 ? '#059669' : '#B45309';
    const outSub = out == null ? null : out > 0 ? null : out < 0 ? t('payment.overpaid', { amount: fmtMoney(-out) }) : t('payment.paidInFull');

    return (
        <SectionCard title={t('payment.title')} action={action} sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', gap: 2, px: 2.5, pt: 2, pb: 1.75, borderBottom: '1px solid #F1F5F9' }}>
                <Figure label={t('payment.price')} value={fmtMoney(job.price)} sub={job.price == null ? t('payment.noPrice') : null} />
                <Figure label={t('payment.paid')} value={fmtMoney(job.paidAmount ?? 0)} color="#059669" />
                <Figure label={t('payment.outstanding')} value={out == null ? '—' : fmtMoney(Math.max(out, 0))} color={outColor} sub={outSub} />
            </Box>
            {out != null && out < 0 && (
                <Alert severity="warning" sx={{ mx: 2.5, mt: 1.5, borderRadius: '10px', py: 0.25 }}>
                    {t('payment.overpaidWarning', { amount: fmtMoney(-out) })}
                </Alert>
            )}
            <Box sx={{ px: 2.5, py: 1 }}>
                {payments.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: '#94A3B8', py: 1 }}>{t('payment.none')}</Typography>
                ) : payments.map(p => (
                    <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: '1px solid #F8FAFC', '&:last-child': { borderBottom: 0 } }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }}>{fmtMoney(p.amount)}</Typography>
                            <Typography sx={{ fontSize: 12, color: '#64748B', wordBreak: 'break-word' }}>
                                {fmtDate(p.paidOn)}{p.note ? ` · ${p.note}` : ''}
                            </Typography>
                            {p.createdByName && <Typography sx={{ fontSize: 11.5, color: '#94A3B8' }}>{t('payment.by', { name: p.createdByName })}</Typography>}
                        </Box>
                        <Tooltip title={t('payment.delete')}>
                            <IconButton size="small" onClick={() => setToDelete(p)} aria-label={t('payment.delete')}><DeleteOutlineIcon fontSize="small" /></IconButton>
                        </Tooltip>
                    </Box>
                ))}
            </Box>
            <Box sx={{ px: 2.5, pb: 2 }}>
                <Button fullWidth variant="outlined" color="success" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>{t('payment.add')}</Button>
            </Box>
            <PaymentDialog open={addOpen} job={job} onClose={() => setAddOpen(false)} onConfirm={add} />
            <ConfirmDialog open={!!toDelete} title={t('payment.deleteTitle')}
                           text={toDelete ? t('payment.deleteText', { amount: fmtMoney(toDelete.amount), date: fmtDate(toDelete.paidOn) }) : ''}
                           onConfirm={remove} onClose={() => setToDelete(null)} />
        </SectionCard>
    );
}
